import React, { useEffect, useState } from 'react'
import GradientButton from '../components/GradientButton'
import IconButton from '../components/IconButton'
import KPICard from '../components/KPICard'
import { useNavigate } from 'react-router-dom'
import { readCurrentFarm, updateCurrentFarm } from '../utils/farmWizard'
import { DEFAULT_PRODUCTION_SPECIES, type ProductionSpeciesKey } from '../data/productionSpeciesPresets'

const SPECIES_NAME_TO_PRESET: Record<string, ProductionSpeciesKey> = {
  'Rainbow Trout': 'trout',
  'Nile Tilapia': 'tilapia',
  'Atlantic Salmon': 'salmon',
  'Pacific White Shrimp': 'shrimp'
}

const inferProductionSpeciesKey = (cultureType: string, selectedSpecies: string[]): ProductionSpeciesKey => {
  for (const name of selectedSpecies) {
    const mapped = SPECIES_NAME_TO_PRESET[name]
    if (mapped) return mapped
  }

  if (cultureType.trim().toLowerCase() === 'crustacean') return 'shrimp'
  return DEFAULT_PRODUCTION_SPECIES
}

const SPECIES_MAPPING: Record<string, { name: string; scientific: string; description: string; difficulty: 'Easy' | 'Medium' | 'Hard'; icon: string }[]> = {
  'Fish': [
    { name: 'Nile Tilapia', scientific: 'Oreochromis niloticus', description: 'Hardy freshwater fish, fast growth, disease resistant', difficulty: 'Easy', icon: 'biomas' },
    { name: 'Atlantic Salmon', scientific: 'Salmo salar', description: 'Premium coldwater fish, high market value', difficulty: 'Hard', icon: 'biomas' },
    { name: 'Rainbow Trout', scientific: 'Oncorhynchus mykiss', description: 'Coldwater species, excellent for beginners', difficulty: 'Medium', icon: 'biomas' },
    { name: 'Channel Catfish', scientific: 'Ictalurus punctatus', description: 'Bottom feeder, tolerates various conditions', difficulty: 'Easy', icon: 'biomas' }
  ],
  'Crustacean': [
    { name: 'Pacific White Shrimp', scientific: 'Litopenaeus vannamei', description: 'Most popular farmed shrimp species worldwide', difficulty: 'Medium', icon: 'biomas' },
    { name: 'Giant Freshwater Prawn', scientific: 'Macrobrachium rosenbergii', description: 'Large freshwater crustacean, good market demand', difficulty: 'Medium', icon: 'biomas' },
    { name: 'Blue Swimming Crab', scientific: 'Portunus pelagicus', description: 'Marine crab with high commercial value', difficulty: 'Hard', icon: 'biomas' }
  ],
  'Mollusk': [
    { name: 'Pacific Oyster', scientific: 'Crassostrea gigas', description: 'Filter feeder, improves water quality', difficulty: 'Easy', icon: 'biomas' },
    { name: 'Blue Mussel', scientific: 'Mytilus edulis', description: 'Cold water species, sustainable protein source', difficulty: 'Easy', icon: 'biomas' },
    { name: 'Manila Clam', scientific: 'Venerupis philippinarum', description: 'Burrowing bivalve, hardy and fast-growing', difficulty: 'Medium', icon: 'biomas' }
  ],
  'Live feed': [
    { name: 'Brine Shrimp', scientific: 'Artemia salina', description: 'Essential live feed for larval fish and shrimp', difficulty: 'Easy', icon: 'feed' },
    { name: 'Rotifers', scientific: 'Brachionus plicatilis', description: 'Microscopic feed for early larval stages', difficulty: 'Medium', icon: 'feed' },
    { name: 'Water Flea', scientific: 'Daphnia magna', description: 'Freshwater crustacean, rich in nutrients', difficulty: 'Easy', icon: 'feed' }
  ]
}

export default function Species(){
  const [options, setOptions] = useState<typeof SPECIES_MAPPING[string]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [currentFarm, setCurrentFarm] = useState<any>(null)
  const navigate = useNavigate()

  useEffect(()=>{
    const farm = readCurrentFarm() ?? {}
    setCurrentFarm(farm)
    const cultureType = (farm as any).typeCulture || ''
    setOptions(SPECIES_MAPPING[cultureType] || [])
    if ((farm as any).species) {
      setSelected((farm as any).species)
    }
  },[])

  function toggleSpecies(speciesName: string){
    setSelected(prev => 
      prev.includes(speciesName) 
        ? prev.filter(x => x !== speciesName) 
        : [...prev, speciesName]
    )
  }

  function onNext(){
    if(selected.length === 0) return alert('Please select at least one species')
    const cultureType = String((currentFarm as any)?.typeCulture ?? '')
    const productionSpeciesKey = inferProductionSpeciesKey(cultureType, selected)
    const next = updateCurrentFarm((prev) => (
      prev
        ? ({
          ...prev,
          species: selected,
          metadata: {
            ...(prev.metadata ?? {}),
            productionSpeciesKey
          }
        } as any)
        : ({
          species: selected,
          metadata: {
            productionSpeciesKey
          }
        } as any)
    ))
    setCurrentFarm(next)
    navigate('/stage')
  }

  const getDifficultyColor = (difficulty: string) => {
    switch(difficulty) {
      case 'Easy': return '#10b981'
      case 'Medium': return '#f59e0b'
      case 'Hard': return '#ef4444'
      default: return '#6b7280'
    }
  }

  const containerStyle: React.CSSProperties = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: 'Inter, system-ui, sans-serif'
  }

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px'
  }

  const titleStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }

  const subtitleStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#6b7280',
    lineHeight: '1.6'
  }

  const progressStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
    padding: '12px 16px',
    background: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd'
  }

  const progressTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#0369a1',
    fontWeight: '500'
  }

  const formCardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    padding: '32px'
  }

  const speciesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '16px',
    marginBottom: '32px'
  }

  const speciesCardStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '20px',
    border: `2px solid ${isSelected ? '#08B8D3' : '#e5e7eb'}`,
    borderRadius: '12px',
    background: isSelected ? '#f0f9ff' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative'
  })

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '32px'
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>
          🐟 Species Selection
        </h2>
        <p style={subtitleStyle}>
          Choose the aquatic species for <strong>{currentFarm?.name || 'your farm'}</strong> ({currentFarm?.typeCulture || 'culture type'}). 
          You can select multiple species to create a diversified operation.
        </p>
      </div>

      {/* Progress Indicator */}
      <div style={progressStyle}>
        <span style={{ fontSize: '16px' }}>🧬</span>
        <span style={progressTextStyle}>Step 3 of 5: Species Selection</span>
      </div>

      {/* Form Card */}
      <div style={formCardStyle}>
        {options.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>No Species Available</h3>
            <p>No species options found for the selected culture type. Please go back and select a culture type first.</p>
            <GradientButton 
              variant="secondary"
              onClick={() => navigate('/type-culture')}
              style={{ marginTop: '16px' }}
            >
              ← Back to Culture Type
            </GradientButton>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '8px' 
              }}>
                Available Species for {currentFarm?.typeCulture}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Selected: {selected.length} species
              </p>
            </div>

            <div style={speciesGridStyle}>
              {options.map(species => (
                <div
                  key={species.name}
                  style={speciesCardStyle(selected.includes(species.name))}
                  onClick={() => toggleSpecies(species.name)}
                  onMouseEnter={(e) => {
                    if (!selected.includes(species.name)) {
                      e.currentTarget.style.borderColor = '#bae6fd'
                      e.currentTarget.style.background = '#f8fafc'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selected.includes(species.name)) {
                      e.currentTarget.style.borderColor = '#e5e7eb'
                      e.currentTarget.style.background = '#ffffff'
                    }
                  }}
                >
                  {/* Selection Indicator */}
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${selected.includes(species.name) ? '#08B8D3' : '#d1d5db'}`,
                    background: selected.includes(species.name) ? '#08B8D3' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selected.includes(species.name) && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#ffffff'
                      }} />
                    )}
                  </div>

                  {/* Species Info */}
                  <div style={{ marginBottom: '12px' }}>
                    <h4 style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#1f2937', 
                      marginBottom: '4px' 
                    }}>
                      {species.name}
                    </h4>
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#6b7280', 
                      fontStyle: 'italic',
                      marginBottom: '8px'
                    }}>
                      {species.scientific}
                    </p>
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#374151', 
                      lineHeight: '1.5' 
                    }}>
                      {species.description}
                    </p>
                  </div>

                  {/* Difficulty Badge */}
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#ffffff',
                    background: getDifficultyColor(species.difficulty)
                  }}>
                    {species.difficulty} to manage
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Species Summary */}
            {selected.length > 0 && (
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#166534', 
                  marginBottom: '8px' 
                }}>
                  ✅ Selected Species ({selected.length})
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px' 
                }}>
                  {selected.map(speciesName => (
                    <span
                      key={speciesName}
                      style={{
                        padding: '4px 8px',
                        background: '#16a34a',
                        color: '#ffffff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {speciesName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={buttonGroupStyle}>
              <GradientButton 
                variant="primary"
                size="lg"
                fullWidth
                onClick={onNext}
                disabled={selected.length === 0}
                icon="biomas"
              >
                Next: Growth Stages →
              </GradientButton>
            </div>

            <div style={buttonGroupStyle}>
              <GradientButton 
                variant="secondary"
                size="md"
                onClick={() => navigate('/type-culture')}
                icon="settings"
              >
                ← Back to Culture Type
              </GradientButton>
              
              <div style={{ marginLeft: 'auto' }}>
                <IconButton
                  icon="chat"
                  variant="ghost"
                  size="md"
                  onClick={() => alert('Help: Select one or more species based on your culture type, market demand, and management capabilities. Difficulty levels indicate care requirements.')}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Species Summary Cards */}
      {selected.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginTop: '32px'
        }}>
          <KPICard
            title="Species Selected"
            value={selected.length}
            icon="biomas"
            variant="success"
          />
          
          <KPICard
            title="Easy to Manage"
            value={options.filter(s => selected.includes(s.name) && s.difficulty === 'Easy').length}
            icon="tasks"
            variant="success"
          />
          
          <KPICard
            title="Medium Difficulty"
            value={options.filter(s => selected.includes(s.name) && s.difficulty === 'Medium').length}
            icon="dashboard-chart"
            variant="warning"
          />
          
          <KPICard
            title="High Difficulty"
            value={options.filter(s => selected.includes(s.name) && s.difficulty === 'Hard').length}
            icon="bell"
            variant="danger"
          />
        </div>
      )}
    </div>
  )
}
